import { useCallback, useEffect, useRef, useState } from 'react'

import { usePageVisibilityListener } from './usePageVisibilityListener'

export function usePolling<ResponseType>(
  fetchFn: () => Promise<ResponseType>,
  interval: number,
  options?: { pauseWhenHidden?: boolean; maxRetries?: number },
): ResponseType | undefined {
  const [data, setData] = useState<ResponseType>()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const failedAttemptsRef = useRef(0)

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
  }, [])

  const poll = useCallback(async () => {
    try {
      const result = await fetchFn()
      setData(result)
      failedAttemptsRef.current = 0
      timeoutRef.current = setTimeout(poll, interval)
    } catch {
      failedAttemptsRef.current++

      if (options?.maxRetries && failedAttemptsRef.current >= options.maxRetries) {
        stopPolling()
        return
      }
      timeoutRef.current = setTimeout(poll, interval)
    }
  }, [fetchFn, interval, options?.maxRetries, stopPolling])

  useEffect(() => {
    poll()
    return stopPolling
  }, [poll, stopPolling])

  usePageVisibilityListener({
    disabled: !options?.pauseWhenHidden,
    onVisible: poll,
    onHidden: stopPolling,
  })

  return data
}
